/**
 * @file Frontend API client — adminPageService.
 */

import axiosClient from "../api/axiosClient";
import { API_URLS } from "../constants/apiUrls";
import type { PublicPage, PublicPageSection } from "./publicPageService";

/** Type definition — Admin Page. */
export type AdminPage = PublicPage & {
  id?: string | number;
};

type AdminPageResponse = {
  success?: boolean;
  data?: AdminPage;
  message?: string;
};

type AdminPageListResponse = {
  success?: boolean;
  data?: AdminPage[];
  message?: string;
};

function coercePage(maybe: any): AdminPage | null {
  if (!maybe) return null;
  if (typeof maybe !== "object") return null;
  if (!maybe.slug) return null;
  return {
    slug: String(maybe.slug),
    title: String(maybe.title || ""),
    sections: Array.isArray(maybe.sections)
      ? (maybe.sections as PublicPageSection[]).map((s: any) => ({
          heading: s?.heading ? String(s.heading) : undefined,
          body: String(s?.body || ""),
        }))
      : undefined,
    content: maybe.content ? String(maybe.content) : undefined,
    updatedAt: maybe.updatedAt ? String(maybe.updatedAt) : undefined,
    id: maybe.id,
  };
}

/** Exported const — admin Page Service. */
export const adminPageService = {
  list: async (): Promise<AdminPage[]> => {
    const res = await axiosClient.get<AdminPageListResponse>(API_URLS.ADMIN.PAGES.LIST);
    const data = (res.data as any)?.data ?? res.data;
    if (Array.isArray(data)) return data.map(coercePage).filter(Boolean) as AdminPage[];
    if (Array.isArray((res.data as any)?.data)) {
      return ((res.data as any).data as any[])
        .map(coercePage)
        .filter(Boolean) as AdminPage[];
    }
    return [];
  },

  getBySlug: async (slug: string): Promise<AdminPage | null> => {
    const res = await axiosClient.get<AdminPageResponse>(API_URLS.ADMIN.PAGES.BY_SLUG(slug));
    const maybe = (res.data as any)?.data ?? res.data;
    return coercePage(maybe);
  },

  upsert: async (slug: string, payload: Pick<AdminPage, "title" | "sections" | "content">) => {
    const res = await axiosClient.put(API_URLS.ADMIN.PAGES.BY_SLUG(slug), payload);
    return res.data;
  },
};

